import { useEffect, useMemo, useState, useCallback } from 'react'
import { useAdminAuthStore } from '@/domains/admin/stores'
import { USER_PERMISSIONS, ROLE_PERMISSIONS, USER_ROLES, mapPermissionNumbersToKeys } from '@shared/constants/user-constants'
import type { UserPermission, UserRole } from '@shared/constants/user-constants'
import { usersApi } from '@admin/api/users'
import { useQuery } from '@tanstack/react-query'
import { ADMIN_ROUTES } from '@admin/constants/routes'

/**
 * Custom hook for managing user permissions based on their role
 */
export function usePermissions() {
  const { user } = useAdminAuthStore()
  const [backendPermissions, setBackendPermissions] = useState<UserPermission[] | null>(null)
  const [isPermissionsLoaded, setIsPermissionsLoaded] = useState<boolean>(false)

  const userRole = useMemo(() => {
    return user?.role as UserRole || USER_ROLES.USER
  }, [user?.role])

  const roleBasedPermissions = useMemo<UserPermission[]>(
    () => (ROLE_PERMISSIONS[userRole] as UserPermission[]) || [],
    [userRole],
  )
  const userPermissions = useMemo<UserPermission[]>(
    () => backendPermissions ?? roleBasedPermissions,
    [backendPermissions, roleBasedPermissions],
  )

  const permissionsQuery = useQuery({
    queryKey: ['permissions', user?.id],
    queryFn: async () => {
      const res = await usersApi.getCurrentUserPermissions()
      if (res.statusCode === 200 && Array.isArray(res.data)) {
        return mapPermissionNumbersToKeys(res.data)
      }
      return null
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    retry: 1,
  })

  useEffect(() => {
    if (!user) {
      setBackendPermissions(null)
      setIsPermissionsLoaded(true)
      return
    }
    
    if (permissionsQuery.isLoading) {
      setIsPermissionsLoaded(false)
      return
    }
    
    if (permissionsQuery.data !== undefined) {
      setBackendPermissions(permissionsQuery.data ?? null)
      setIsPermissionsLoaded(true)
    }
  }, [user, permissionsQuery.isLoading, permissionsQuery.data])

  const hasPermission = useCallback((permission: UserPermission) => {
    return userPermissions.includes(permission)
  }, [userPermissions])

  const hasAnyPermission = useCallback((permissions: UserPermission[]) => {
    return permissions.some(permission => userPermissions.includes(permission))
  }, [userPermissions])

  const hasAllPermissions = useCallback((permissions: UserPermission[]) => {
    return permissions.every(permission => userPermissions.includes(permission))
  }, [userPermissions])

  const getFirstAvailablePage = useCallback(() => {
    const pagePriority = [
      {
        path: ADMIN_ROUTES.TRANSACTIONS,
        permission: USER_PERMISSIONS.VIEW_TRANSACTIONS,
        label: 'Transactions'
      },
      {
        path: ADMIN_ROUTES.BILLING,
        permission: USER_PERMISSIONS.VIEW_REPORTS,
        label: 'Reports'
      },
      {
        path: ADMIN_ROUTES.USERS,
        permission: USER_PERMISSIONS.VIEW_USER,
        label: 'Users'
      },
      {
        path: ADMIN_ROUTES.F1_SETTINGS,
        permission: USER_PERMISSIONS.VIEW_ORGANIZATION,
        label: 'F1 Settings'
      }
    ]

    return pagePriority.find(page => hasPermission(page.permission)) || null
  }, [hasPermission])

  const clearPermissionsCache = useCallback(() => {
    setBackendPermissions(null)
    setIsPermissionsLoaded(false)
    if (typeof window !== 'undefined' && (window as any).__QUERY_CLIENT__) {
      try {
        const queryClient = (window as any).__QUERY_CLIENT__
        queryClient.invalidateQueries({ queryKey: ['permissions'] })
      } catch (e) {
        console.log('Failed to invalidate permissions query:', e)
      }
    }
  }, [])

  const canCreateUsers = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.CREATE_USER)
  }, [hasPermission])

  const canDeleteUsers = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.DELETE_USER)
  }, [hasPermission])

  const canEditUsers = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.EDIT_USER)
  }, [hasPermission])

  const canViewReports = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.VIEW_REPORTS)
  }, [hasPermission])

  const canViewTransactions = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.VIEW_TRANSACTIONS)
  }, [hasPermission])

  const canViewAllLocations = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.VIEW_ALL_LOCATIONS)
  }, [hasPermission])

  const canViewWorkstations = useMemo(() => {
    return hasPermission(USER_PERMISSIONS.VIEW_WORKSTATIONS)
  }, [hasPermission])

  const isSuperAdmin = useMemo(() => {
    return userRole === USER_ROLES.SUPERADMIN
  }, [userRole])

  const isAdmin = useMemo(() => {
    return userRole === USER_ROLES.ADMIN
  }, [userRole])

  const isViewer = useMemo(() => {
    return userRole === USER_ROLES.VIEWER
  }, [userRole])

  const isUser = useMemo(() => {
    return userRole === USER_ROLES.USER
  }, [userRole])

  return {
    userRole,
    userPermissions,
    isPermissionsLoaded,
    
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    getFirstAvailablePage,
    clearPermissionsCache,
    
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canViewReports,
    canViewTransactions,
    canViewAllLocations,
    canViewWorkstations,
    
    isSuperAdmin,
    isAdmin,
    isViewer,
    isUser
  }
}
